import './LiveEndStatistics.sass';

import React, { FC, useEffect, useMemo } from 'react';
import { intervalToDuration } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslator } from '$hooks';
import { Button, List, ListItem, ScrollView } from '$uikit';
import { liveActions, liveSelector } from '$store/live';
import { Loader } from '$shared/components';
import { classNames } from '$utils';
import { hideModal } from '$navigation/helper';

export const LiveEndStatistics: FC = () => {
  const t = useTranslator();
  const dispatch = useDispatch();
  const { endStatistics } = useSelector(liveSelector);

  const formattedTime = useMemo(() => {
    let time: string = '';

    if (endStatistics?.duration) {
      const nowDate = new Date(0);
      const durationDate = new Date(endStatistics.duration * 1000);
      const duration = intervalToDuration({
        start: nowDate,
        end: durationDate,
      });
      const hours = duration?.hours
        ? `${t('time_hours', { amount: duration.hours })} `
        : '';
      const minutes = duration?.minutes
        ? `${t('time_minutes', { amount: duration.minutes })} `
        : '';
      const seconds = `${t('time_seconds', { amount: duration.seconds })}`;
      time = `${hours}${minutes}${seconds}`;
    }

    return time;
  }, [endStatistics?.duration]);

  useEffect(() => {
    return () => {
      dispatch(liveActions.closeLiveEndStatistics());
    };
  }, []);

  const handleOnClick = () => {
    hideModal();
  };

  function renderContent() {
    return (
      <>
        <div className="Placeholder__title LiveEndStatistics__title">
          {t('live_end_statistics_title')}
        </div>
        <List className="LiveEndStatistics__list">
          <ListItem
            description={t('live_end_statistics_stream_time')}
            descriptionDisablePadding
            after={
              <p
                className={classNames({
                  ListItem__label: true,
                  LiveEndStatistics__text: true,
                })}
              >
                {endStatistics?.duration && formattedTime}
              </p>
            }
          />
          <ListItem
            description={t('live_end_statistics_audience_title')}
            descriptionDisablePadding
            multipleLine
            after={
              <p
                className={classNames({
                  ListItem__label: true,
                  LiveEndStatistics__text: true,
                })}
              >
                {endStatistics?.audience === 'public'
                  ? t('live_end_statistics_audience_type_public')
                  : endStatistics?.audience === 'erotic'
                  ? t('live_end_statistics_audience_type_erotic')
                  : t('live_end_statistics_audience_type_sponsor')}
              </p>
            }
          />
          <ListItem
            description={t('live_end_statistics_people_watching_title')}
            descriptionDisablePadding
            after={
              <p
                className={classNames({
                  ListItem__label: true,
                  LiveEndStatistics__text: true,
                })}
              >
                {endStatistics?.watched}
              </p>
            }
          />
          <ListItem
            description={t('live_end_statistics_money_donated_title')}
            descriptionDisablePadding
            after={
              <p
                className={classNames({
                  ListItem__label: true,
                  LiveEndStatistics__text: true,
                })}
              >
                €{endStatistics?.donated}
              </p>
            }
          />
        </List>
      </>
    );
  }

  return (
    <ScrollView>
      <div className="LiveEndStatistics">
        <div className="LiveEndStatistics__content">
          {endStatistics ? renderContent() : <Loader isWrapped />}
        </div>
        <div className="LiveEndStatistics__footer">
          <Button
            className="LiveEndStatistics__footer__button"
            onClick={handleOnClick}
            stretched
          >
            {t('live_end_statistics_button_close_title')}
          </Button>
        </div>
      </div>
    </ScrollView>
  );
};
